import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import SearchableSelect from '@/shein-platform/modules/products/components/SearchableSelect';
import {
  collectUniquePackageSizes,
  packageBatchHasAnyValue,
  packagePatchHasValue,
} from '@/shein-platform/utils/sheinPackageBatch';
import i18n from '@/i18n';
import { useTranslation } from 'react-i18next';
import { localizeOptions } from '@/i18n/chartText';
import { SHEIN_QTY_KEYS, SKU_PKG_TYPE_KEYS } from '@/i18n/pkgOptionKeys';

function pkgText(key, fallback, options) {
  if (!i18n.isInitialized) return fallback;
  return i18n.t(key, { ns: 'workbench', defaultValue: fallback, ...(options || {}) }) || fallback;
}

const PKG_TITLE_KEYS = {
  weight: 'publishPkg.titleWeight',
  dims: 'publishPkg.titlePkgSize',
  quantity: 'publishPkg.titlePkgQty',
  packageType: 'publishPkg.titlePkgType',
};

export const PKG_BATCH_TITLES = {
  weight: '批量填写重量',
  dims: '批量填写包装尺寸',
  quantity: '批量填写包装件数',
  packageType: '批量填写包装类型',
};

export const WEIGHT_UNIT_OPTIONS = [
  { value: 'g', label: 'g' },
  { value: 'lb', label: 'lb' },
  { value: 'Oz', label: 'Oz' },
];

export const DIM_UNIT_OPTIONS = [
  { value: 'cm', label: 'cm' },
  { value: 'Inch', label: 'Inch' },
  { value: 'Ft', label: 'Ft' },
];

export const QUANTITY_UNIT_OPTIONS = [
  { value: '1', label: '件' },
  { value: '2', label: '双' },
  { value: '3', label: '套' },
];

export const PACKAGE_TYPE_OPTIONS = [
  { value: '3', label: '硬包装' },
  { value: '2', label: '软包装+硬物' },
  { value: '1', label: '软包装+软物' },
  { value: '4', label: '抽真空' },
];

function emptyRow() {
  return {
    weight: '',
    length: '',
    width: '',
    height: '',
    quantity: '',
    quantityUnit: '1',
    packageType: '',
  };
}

function hasAnySku(skcList) {
  return (Array.isArray(skcList) ? skcList : []).some((skc) => (skc?.skuRows || []).length > 0);
}

export default function PackageBatchModal({
  mode,
  skcList = [],
  sizeAttr = null,
  onClose,
  onConfirm,
  weightUnit: initialWeightUnit = 'g',
  dimUnit: initialDimUnit = 'cm',
}) {
  useTranslation('workbench');
  const sizes = useMemo(() => collectUniquePackageSizes(skcList, sizeAttr), [skcList, sizeAttr]);
  const canApply = hasAnySku(skcList);
  const [weightUnit, setWeightUnit] = useState(initialWeightUnit || 'g');
  const [dimUnit, setDimUnit] = useState(initialDimUnit || 'cm');
  const [rows, setRows] = useState(() => {
    const init = {};
    sizes.forEach((size) => {
      init[size.key] = { ...emptyRow(), ...(size.seed || {}) };
    });
    return init;
  });
  const [sharedRow, setSharedRow] = useState(() => ({
    ...emptyRow(),
    ...(sizes[0]?.seed || {}),
  }));
  const inputRef = useRef(null);
  const isWeight = mode === 'weight';
  const isQuantity = mode === 'quantity';
  const isPackageType = mode === 'packageType';
  const isDims = !isWeight && !isQuantity && !isPackageType;
  const isSharedAll = isDims || isPackageType;

  useEffect(() => {
    setWeightUnit(initialWeightUnit || 'g');
    setDimUnit(initialDimUnit || 'cm');
  }, [initialWeightUnit, initialDimUnit, mode]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [onClose]);

  const updateRow = (key, patch) => {
    setRows((prev) => ({
      ...prev,
      [key]: { ...emptyRow(), ...(prev[key] || {}), ...patch },
    }));
  };

  const updateShared = (patch) => {
    setSharedRow((prev) => ({ ...emptyRow(), ...prev, ...patch }));
  };

  const submit = () => {
    if (isSharedAll) {
      const patch = isPackageType
        ? { packageType: String(sharedRow.packageType || '') }
        : {
          length: String(sharedRow.length || '').trim(),
          width: String(sharedRow.width || '').trim(),
          height: String(sharedRow.height || '').trim(),
        };
      if (!packagePatchHasValue(patch)) {
        onConfirm?.(null);
        return;
      }
      onConfirm?.({
        applyAll: true,
        patch,
        dimUnit: String(dimUnit || 'cm'),
      });
      return;
    }

    const bySize = {};
    sizes.forEach((size) => {
      const row = rows[size.key] || emptyRow();
      if (isWeight) {
        bySize[size.key] = { weight: String(row.weight || '').trim() };
        return;
      }
      bySize[size.key] = {
        quantity: String(row.quantity || '').trim(),
        quantityUnit: String(row.quantityUnit || '1'),
      };
    });
    if (!packageBatchHasAnyValue(bySize)) {
      onConfirm?.(null);
      return;
    }
    onConfirm?.({
      bySize,
      weightUnit: String(weightUnit || 'g'),
      dimUnit: String(dimUnit || 'cm'),
    });
  };

  const enterSubmit = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="spo-batch-modal" role="dialog" aria-modal="true">
      <div
        className={`spo-batch-modal__panel spo-batch-modal__panel--pkg-size${isDims ? ' spo-batch-modal__panel--pkg-dims' : ''}${isSharedAll ? ' spo-batch-modal__panel--pkg-shared' : ''}`}
      >
        <div className="spo-batch-modal__head">
          <strong>{pkgText(PKG_TITLE_KEYS[mode] || 'publishPkg.batchFill', PKG_BATCH_TITLES[mode] || fixedUi("批量填写"))}</strong>
          <button type="button" className="spo-batch-modal__close" aria-label={pkgText('moduleShell.close', fixedUi("关闭"))} onClick={onClose}>×</button>
        </div>
        <div className="spo-batch-modal__body">
          <p className="spo-batch-modal__hint">
            {isSharedAll
              ? pkgText('publishPkg.sharedHint', '填写一套值，将同步到全部颜色与尺码的 SKU 行')
              : pkgText('publishPkg.bySizeHint', '按去重尺码填写，将同步到所有颜色的对应尺码行')}
          </p>
          {!canApply ? (
            <p className="spo-batch-modal__hint">{pkgText('publishPkg.pickSizeFirst', fixedUi("请先选择规格尺码"))}</p>
          ) : isSharedAll ? (
            <>
              {isDims ? (
                <div className="spo-pkg-inline spo-pkg-size-unit">
                  <span className="spo-field__label">{pkgText('publishPkg.dimUnit', fixedUi("尺寸单位"))}</span>
                  <SearchableSelect
                    className="spo-select--unit"
                    value={dimUnit}
                    allowClear={false}
                    onChange={(v) => setDimUnit(v)}
                    options={DIM_UNIT_OPTIONS}
                  />
                </div>
              ) : null}
              {isPackageType ? (
                <SearchableSelect
                  value={sharedRow.packageType}
                  allowClear
                  placeholder={pkgText('publishPkg.pickPkgType', '请选择包装类型')}
                  onChange={(v) => updateShared({ packageType: v == null ? '' : String(v) })}
                  options={localizeOptions(PACKAGE_TYPE_OPTIONS, SKU_PKG_TYPE_KEYS)}
                />
              ) : (
                <div className="spo-pkg-inline spo-pkg-inline--dims">
                  <input
                    ref={inputRef}
                    className="spo-input spo-pkg-inline__input"
                    value={sharedRow.length}
                    inputMode="decimal"
                    placeholder={pkgText('publishPkg.len', fixedUi("长"))}
                    onChange={(e) => updateShared({ length: e.target.value })}
                  />
                  <span className="spo-pkg-inline__x">×</span>
                  <input
                    className="spo-input spo-pkg-inline__input"
                    value={sharedRow.width}
                    inputMode="decimal"
                    placeholder={pkgText('publishPkg.wid', fixedUi("宽"))}
                    onChange={(e) => updateShared({ width: e.target.value })}
                  />
                  <span className="spo-pkg-inline__x">×</span>
                  <input
                    className="spo-input spo-pkg-inline__input"
                    value={sharedRow.height}
                    inputMode="decimal"
                    placeholder={pkgText('publishPkg.hei', fixedUi("高"))}
                    onChange={(e) => updateShared({ height: e.target.value })}
                    onKeyDown={enterSubmit}
                  />
                </div>
              )}
            </>
          ) : (
            <>
              {isWeight ? (
                <div className="spo-pkg-inline spo-pkg-size-unit">
                  <span className="spo-field__label">{pkgText('publishPkg.weightUnit', fixedUi("重量单位"))}</span>
                  <SearchableSelect
                    className="spo-select--unit"
                    value={weightUnit}
                    allowClear={false}
                    onChange={(v) => setWeightUnit(v)}
                    options={WEIGHT_UNIT_OPTIONS}
                  />
                </div>
              ) : null}
              {!sizes.length ? (
                <p className="spo-batch-modal__hint">{pkgText('publishPkg.pickSizeFirst', fixedUi("请先选择规格尺码"))}</p>
              ) : (
                <div className="spo-pkg-size-rows">
                  {sizes.map((size, index) => {
                    const row = rows[size.key] || emptyRow();
                    return (
                      <div key={size.key} className="spo-pkg-size-row">
                        <span className="spo-pkg-size-row__name" title={size.label}>{size.label}</span>
                        {isWeight ? (
                          <input
                            ref={index === 0 ? inputRef : undefined}
                            className="spo-input"
                            value={row.weight}
                            inputMode="decimal"
                            placeholder={pkgText('publishPkg.like200', '如 200')}
                            onChange={(e) => updateRow(size.key, { weight: e.target.value })}
                            onKeyDown={enterSubmit}
                          />
                        ) : (
                          <div className="spo-pkg-inline">
                            <input
                              ref={index === 0 ? inputRef : undefined}
                              className="spo-input spo-pkg-inline__input spo-pkg-inline__input--qty"
                              value={row.quantity}
                              inputMode="numeric"
                              placeholder={pkgText('publishPkg.phQty', fixedUi("件数"))}
                              onChange={(e) => updateRow(size.key, { quantity: e.target.value })}
                              onKeyDown={enterSubmit}
                            />
                            <SearchableSelect
                              className="spo-select--unit"
                              value={row.quantityUnit || '1'}
                              allowClear={false}
                              onChange={(v) => updateRow(size.key, { quantityUnit: v })}
                              options={localizeOptions(QUANTITY_UNIT_OPTIONS, SHEIN_QTY_KEYS)}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={onClose}>{pkgText('shop.cancel', fixedUi("取消"))}</button>
          <button
            type="button"
            className="spo-btn spo-btn--primary spo-btn--sm"
            onClick={submit}
            disabled={isSharedAll ? !canApply : !sizes.length}
          >
            {pkgText('moduleShell.confirmOk', '确定')}
          </button>
        </div>
      </div>
    </div>
  );
}
